import { SortingState } from "@tanstack/react-table";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useManageTable } from "hooks";
import { GetListRequestParams } from "libs/api";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { useGetResellers } from "../apis";
import {
  AGE_FROM_FILTER_SEARCH_PARAMETER,
  AGE_TO_FILTER_SEARCH_PARAMETER,
  CANCELED_FROM_FILTER_SEARCH_PARAMETER,
  CANCELED_TO_FILTER_SEARCH_PARAMETER,
  DELIVERED_FROM_FILTER_SEARCH_PARAMETER,
  DELIVERED_TO_FILTER_SEARCH_PARAMETER,
  EARNINGS_FROM_FILTER_SEARCH_PARAMETER,
  EARNINGS_TO_FILTER_SEARCH_PARAMETER,
  GENDER_FILTER_SEARCH_PARAMETER,
  RETURNED_FROM_FILTER_SEARCH_PARAMETER,
  RETURNED_TO_FILTER_SEARCH_PARAMETER,
  ResellersFilterGenderValues,
  ResellersFilterVerificationValues,
  SALES_FROM_FILTER_SEARCH_PARAMETER,
  SALES_TO_FILTER_SEARCH_PARAMETER,
  VERIFICATION_FILTER_SEARCH_PARAMETER,
  DELETE_STATUS_FILTER_SEARCH_PARAMETER,
  ResellersFilterDeleteStatusValues
} from "../constants";

export interface UseResellersListProps {
  defaultSorting?: SortingState;
}

const useResellersList = ({
  defaultSorting = [{ id: "RegistrationDate", desc: true }]
}: UseResellersListProps = {}) => {
  const {
    page,
    searchValue,
    debouncedSearch,
    sorting,
    setSorting,
    setSearchValue,
    setPage
  } = useManageTable({
    defaultSorting
  });
  const { activeTab } = useParams();

  const [searchParams] = useSearchParams();
  const verification = searchParams.get(VERIFICATION_FILTER_SEARCH_PARAMETER);
  const gender = searchParams.get(GENDER_FILTER_SEARCH_PARAMETER);
  const ageFrom = searchParams.get(AGE_FROM_FILTER_SEARCH_PARAMETER);
  const ageTo = searchParams.get(AGE_TO_FILTER_SEARCH_PARAMETER);
  const totalEarningsFrom = searchParams.get(
    EARNINGS_FROM_FILTER_SEARCH_PARAMETER
  );
  const totalEarningsTo = searchParams.get(EARNINGS_TO_FILTER_SEARCH_PARAMETER);
  const totalSalesFrom = searchParams.get(SALES_FROM_FILTER_SEARCH_PARAMETER);
  const totalSalesTo = searchParams.get(SALES_TO_FILTER_SEARCH_PARAMETER);
  const numberOfCanceledOrdersFrom = searchParams.get(
    CANCELED_FROM_FILTER_SEARCH_PARAMETER
  );
  const numberOfCanceledOrdersTo = searchParams.get(
    CANCELED_TO_FILTER_SEARCH_PARAMETER
  );
  const numberOfDeliveredOrdersFrom = searchParams.get(
    DELIVERED_FROM_FILTER_SEARCH_PARAMETER
  );
  const numberOfDeliveredOrdersTo = searchParams.get(
    DELIVERED_TO_FILTER_SEARCH_PARAMETER
  );
  const numberOfReturnedOrdersFrom = searchParams.get(
    RETURNED_FROM_FILTER_SEARCH_PARAMETER
  );
  const numberOfReturnedOrdersTo = searchParams.get(
    RETURNED_TO_FILTER_SEARCH_PARAMETER
  );
  const deleteStatus = searchParams.get(DELETE_STATUS_FILTER_SEARCH_PARAMETER);

  const getIsVerifiedParameter = () => {
    switch (verification) {
      case ResellersFilterVerificationValues.Verified:
        return true;

      case ResellersFilterVerificationValues.Unverified:
        return false;

      default:
        return null;
    }
  };

  const getIsDeletedParameter = () => {
    switch (deleteStatus) {
      case ResellersFilterDeleteStatusValues.Deleted:
        return true;

      case ResellersFilterDeleteStatusValues.Active:
      case null:
        return false;

      default:
        return undefined;
    }
  };

  const requestParams: GetListRequestParams = useMemo(
    () => ({
      page,
      take: GET_LIST_REQUEST_DEFAULT_LIMIT,
      search: debouncedSearch,
      orderBy: sorting[0]?.id ?? null,
      order: sorting[0] ? (sorting[0]?.desc ? "DESC" : "ASC") : "DESC",
      isVerified: getIsVerifiedParameter(),
      isDeleted: getIsDeletedParameter(),
      gender: gender !== ResellersFilterGenderValues.All ? gender : null,
      levels: activeTab !== "all" ? activeTab : null,
      ageFrom,
      ageTo,
      totalEarningsFrom,
      totalEarningsTo,
      totalSalesFrom,
      totalSalesTo,
      numberOfCanceledOrdersFrom,
      numberOfCanceledOrdersTo,
      numberOfDeliveredOrdersFrom,
      numberOfDeliveredOrdersTo,
      numberOfReturnedOrdersFrom,
      numberOfReturnedOrdersTo
    }),
    [
      page,
      debouncedSearch,
      sorting,
      verification,
      gender,
      ageFrom,
      ageTo,
      totalEarningsFrom,
      totalEarningsTo,
      totalSalesFrom,
      totalSalesTo,
      numberOfCanceledOrdersFrom,
      numberOfCanceledOrdersTo,
      numberOfDeliveredOrdersFrom,
      numberOfDeliveredOrdersTo,
      numberOfReturnedOrdersFrom,
      numberOfReturnedOrdersTo,
      activeTab,
      deleteStatus
    ]
  );

  const { resellers, meta, isLoading, refetch } =
    useGetResellers(requestParams);

  return {
    resellers,
    totalItems: meta?.itemCount,
    searchValue,
    page,
    isLoading,
    sorting,
    setSorting,
    setSearchValue,
    setPage,
    refetch
  };
};

export default useResellersList;
